<template>
  <div>
    <div class="export-and-shipped">
      <BaseTableList
          v-show="$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.EXPORT_LIST)"
          class="table export-table"
          :table-set-list="exportList"
          :display-items="_exportData"
          :row-class-name="$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.DETAIL) ? 'clickable' : ''"
          @row-click="onExportListRowClick">
        <template slot="title">
          <div class="table-header">
            <h3>出荷指図情報</h3>
          </div>
        </template>
      </BaseTableList>
      <BaseTableList
          v-show="$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.SHIPPED_LIST)"
          class="table shipped-table"
          :table-set-list="shippedList"
          :display-items="shippedData"
          :cell-class-name="cellClassName"
          :row-class-name="$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.DETAIL) ? 'clickable' : ''"
          @row-click="onShippedListRowClick">
        <template slot="title">
          <div class="table-header">
            <h3>出荷実績情報</h3>
          </div>
        </template>
      </BaseTableList>
    </div>

    <!-- 出荷指図/出荷実績明細ダイアログ -->
    <SlipDialog
        :title="detailModalTitle"
        :type="detailModalType"
        :visible.sync="detailModalVisible"
        :detail-data="detailModalData"
        :detail-list-data="detailListModalData" />
  </div>
</template>

<style scoped>
.export-and-shipped .shipped-table >>> td.slip-type-receive {
  color: #67C23A;
}
.export-and-shipped .shipped-table >>> td.slip-type-error {
  color: #ff5a5a;
}
</style>

<script>
import _ from 'lodash'

import * as define from '@/define'
import math from '@/utils/math'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableList from '@/components/molecules/BaseTableList'
import SlipDialog from '@/components/pages/order/SlipDialog'
import { mapGetters } from 'vuex'

const SHIPPED_TAG_INFO = {
  exists: {
    label: '出荷実績あり',
    type: 'success'
  },
  notExists: {
    label: '出荷実績なし',
    type: 'info'
  }

}

export default {
  name: 'OrderDetailExportAndShipped',
  components: {
    BaseTableList,
    SlipDialog
  },
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  props: {
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    exportData: {
      type: Array,
      default: () => []
    },
    shippedData: {
      type: Array,
      default: () => []
    },
    changingNote: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      detailModalType: '',
      detailModalTitle: '',
      detailModalVisible: false,
      detailModalData: [],
      detailListModalData: []
    }
  },
  computed: {
    define: () => define,
    ...mapGetters({
      exportIdFormats: 'exportIdFormats'
    }),
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode'
    }),
    _exportData () {
      return this.exportData.filter(element => element.isAvailable === 1)
    },
    // 指図 テーブルセットリスト
    exportList () {
      return [
        { label: '登録日時', path: 'createdAt', type: 'Date', width: 200,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.CREATED_AT },
        { label: '配送番号', path: this.shipmentId, type: 'String', width: 150, elementType: ELEMENT_TYPE.FUNCTION },
        {
          type: 'String', label: '', path: this.shippedTag, elementType: ELEMENT_TYPE.TAG,
          class: 'center', size: 'small', width: 150,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.STATUS
        },
        { label: '出荷指図ID', path: this.exportId, type: 'String', elementType: ELEMENT_TYPE.FUNCTION,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.LIST_ID },
        { label: '指図日時', path: 'exportedAt', type: 'Date',
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.EXPORTED_AT },
        {
          label: '指図数', path: this.exportQty, type: 'Number', elementType: ELEMENT_TYPE.FUNCTION,
          class: 'right', headerClass: 'right', width: 100,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.QUANTITY
        }
      ]
    },
    shippedList () {
      return [
        { label: '伝票日時', path: 'slipDatetime', type: 'Date', width: 200,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.SLIP_DATETIME },
        { label: '配送番号', path: this.orderShipmentId, type: 'String', width: 150, elementType: ELEMENT_TYPE.FUNCTION },
        { width: 150, funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.SLIP_DATETIME }, // 上部の出荷指図情報と合わせるため幅調整
        { label: '出荷指図ID', path: this.orderExportId, type: 'String', elementType: ELEMENT_TYPE.FUNCTION,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.ORDER_EXPORT_ID },
        { label: '伝票種類', path: 'slipType', type: 'String', codeName: 'd_shipped.slip_type', columnKey: 'slipType',
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.SLIP_TYPE },
        { label: '伝票番号', path: 'slipNo', type: 'String',
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.SLIP_NUMBER },
        { label: '訂正区分', path: 'correctType', type: 'String', codeName: 'd_shipped.correct_type',
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.CORRECT_TYPE },
        { label: '追跡番号', path: 'trackingNumber', type: 'String',
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.TRACKING_NUMBER },
        {
          label: '数量', path: this.shippedQty, type: 'Number', elementType: ELEMENT_TYPE.FUNCTION,
          class: 'right', headerClass: 'right', width: 100,
          funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.QUANTITY
        }
      ]
    }
  },
  methods: {
    exportQty (row) {
      return math.add(_.map(row.items, val => val.quantity))
    },
    exportId (row) {
      let exportId = _.get(row, 'id', '')
      const shippingMethod = _.get(row, 'items.0.orderItem.shipment.shippingMethod', '')
      const format = this.exportIdFormats(this.siteCode).find((format) => format.key === shippingMethod)
      if (format && exportId) {
        exportId = format.toConvert(exportId)
      }
      return exportId
    },
    orderExportId (row) {
      let exportId = _.get(row, 'orderExport.id', '')
      const shippingMethod = _.get(row, 'orderShipment.shippingMethod', '')
      const format = this.exportIdFormats(this.siteCode).find((format) => format.key === shippingMethod)
      if (format && exportId) {
        exportId = format.toConvert(exportId)
      }
      return exportId
    },
    shipmentId (row) {
      return _.get(row, 'items.0.orderItem.shipment.shipmentId', '')
    },
    orderShipmentId (row) {
      return _.get(row, 'orderShipment.shipmentId', '')
    },
    shippedTag (row) {
      this.$log.debug('', row)

      const tags = []
      const shipped = _.find(this.shippedData, val => _.get(val, 'orderExport.id') === row.id)
      const tagInfo = shipped ? SHIPPED_TAG_INFO.exists : SHIPPED_TAG_INFO.notExists
      tags.push({ label: tagInfo.label, type: tagInfo.type })
      return tags
    },
    onExportListRowClick (row) {
      if (this.$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.DETAIL)) {
        this.$log.debug('onExportListRowClick', row)
        this.detailModalVisible = true
        this.detailModalType = 'export'
        this.detailModalTitle = '出荷指図明細'
        this.detailModalData = row
        this.detailListModalData = row.items
      }
    },
    shippedQty (row) {
      return math.add(_.map(row.items, val => val.quantity))
    },
    onShippedListRowClick (row) {
      if (this.$isFunctionShown(define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.DETAIL)) {
        this.$log.debug('onShippedListRowClick', row)
        this.detailModalVisible = true
        this.detailModalType = 'shipped'
        this.detailModalTitle = '出荷実績明細'
        this.detailModalData = row
        this.detailListModalData = row.items
      }
    },
    cellClassName ({ row, column }) {
      if (column.columnKey !== 'slipType') {
        return ''
      }
      switch (row.slipType) {
        case 2: // 返品・移動入荷
        case 4: // 交換返品
          return 'slip-type-receive'
        case 9: // 出荷キャンセル
          return 'slip-type-error'
        default:
          return ''
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/OrderDetailExportAndShipped.vue